import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonateStore } from '@/stores/useImpersonateStore';
import type { Tables, Enums } from '@/integrations/supabase/types';

type Academy = Tables<'academies'>;
type AcademyRole = Enums<'academy_role'>;

export interface AcademyMembership {
  academy: Academy;
  role: AcademyRole;
  studentCount: number;
}

const STORAGE_KEY = 'jedi_academy_id';

/**
 * Priority sort:
 * 1. personal_trainer + gestor
 * 2. gestor/owner with students > 0
 * 3. anything else (e.g. professor)
 */
function priorityScore(m: AcademyMembership): number {
  const isPersonalTrainer = m.academy.business_type === 'personal_trainer';
  const isManager = m.role === 'gestor';
  if (isPersonalTrainer && m.role === 'gestor') return 0;
  if (isManager && m.studentCount > 0) return 1;
  if (isManager) return 2;
  return 3;
}

function sortByPriority(list: AcademyMembership[]): AcademyMembership[] {
  return [...list].sort((a, b) => {
    const sa = priorityScore(a);
    const sb = priorityScore(b);
    if (sa !== sb) return sa - sb;
    // tie-breaker: more students first
    return b.studentCount - a.studentCount;
  });
}

export function useAcademyContext() {
  const { user } = useAuth();
  const { impersonatedUserId, isImpersonating } = useImpersonateStore();
  const effectiveUserId = isImpersonating ? impersonatedUserId : user?.id;
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY)
  );
  const autoSwitchedRef = useRef(false);

  const { data: memberships = [], isLoading } = useQuery({
    queryKey: ['my-academies', effectiveUserId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('academy_members')
        .select('role, academy_id, academies(*)')
        .eq('user_id', effectiveUserId!)
        .eq('is_active', true)
        .not('academies', 'is', null);

      if (error) throw error;
      if (!data?.length) return [];

      const base = data
        .filter((m) => m.academies && (m.academies as any).is_active)
        .map((m) => ({
          academy: m.academies as unknown as Academy,
          role: m.role as AcademyRole,
          studentCount: 0,
        }));

      // Fetch active student counts per academy
      const ids = base.map((b) => b.academy.id);
      if (ids.length > 0) {
        const counts = await Promise.all(
          ids.map(async (id) => {
            const { count } = await supabase
              .from('students')
              .select('id', { count: 'exact', head: true })
              .eq('academy_id', id)
              .is('deleted_at', null);
            return { id, count: count ?? 0 };
          })
        );
        const map = new Map(counts.map((c) => [c.id, c.count]));
        base.forEach((b) => {
          b.studentCount = map.get(b.academy.id) ?? 0;
        });
      }

      return sortByPriority(base);
    },
    enabled: !!effectiveUserId,
    staleTime: 5 * 60 * 1000,
  });

  // Auto-select / auto-switch logic
  useEffect(() => {
    if (isLoading || !memberships.length) return;
    const saved = localStorage.getItem(STORAGE_KEY);
    const top = memberships[0]; // already sorted by priority

    // Nothing saved or saved is invalid → pick highest-priority
    if (!saved || !memberships.some((m) => m.academy.id === saved)) {
      setSelectedId(top.academy.id);
      localStorage.setItem(STORAGE_KEY, top.academy.id);
      return;
    }

    // Saved exists but has 0 students AND there is another with students > 0
    // → auto-switch to the highest-priority populated workspace.
    const savedMembership = memberships.find((m) => m.academy.id === saved);
    const hasPopulated = memberships.some((m) => m.studentCount > 0);
    if (
      savedMembership &&
      savedMembership.studentCount === 0 &&
      hasPopulated &&
      top.academy.id !== saved &&
      !autoSwitchedRef.current
    ) {
      autoSwitchedRef.current = true;
      setSelectedId(top.academy.id);
      localStorage.setItem(STORAGE_KEY, top.academy.id);
      toast.info(`Workspace alterado para "${top.academy.name}"`, {
        description: 'Selecionamos automaticamente a academia com seus alunos.',
      });
    }
  }, [memberships, isLoading]);

  const current = memberships.find((m) => m.academy.id === selectedId);

  const switchAcademy = useCallback((id: string | null) => {
    autoSwitchedRef.current = true; // user took control; stop auto-switching
    setSelectedId(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const allAcademyIds = memberships.map((m) => m.academy.id);

  const getAcademyName = (academyId: string) => {
    const membership = memberships.find((m) => m.academy.id === academyId);
    return membership?.academy.name ?? null;
  };

  return {
    currentAcademy: current?.academy ?? null,
    currentRole: current?.role ?? null,
    academies: memberships,
    allAcademyIds,
    getAcademyName,
    isIndependent: !isLoading && memberships.length === 0,
    isLoading,
    switchAcademy,
  };
}
